
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ExplanationDemo from './components/ExplanationDemo.tsx';
import AuthModal from './components/AuthModal.tsx';
import { trackEvent } from './services/analyticsService.ts';
import { supabase, signOut } from './services/supabaseService.ts';
import { createCheckoutSession, createPortalSession, getSubscriptionStatus } from './services/stripeService.ts';

const App: React.FC = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [authReason, setAuthReason] = useState<string | undefined>(undefined);
  
  const [user, setUser] = useState<any>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

  const hasInitialized = useRef(false);

  const syncState = useCallback(async (forceSession?: any) => {
    try {
      const session = forceSession || (await supabase.auth.getSession()).data.session;
      if (session?.user) {
        setUser(session.user);
        try {
          const status = await getSubscriptionStatus(session.user.id);
          setSubscriptionStatus(status);
        } catch (dbError) {
          console.error("⚠️ HW Bridge: Profile fetch failed", dbError);
        }
      } else {
        setUser(null);
        setSubscriptionStatus(null);
      }
    } catch (error) {
      console.error("❌ HW Bridge: Auth Sync Error", error);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isInitializing) setIsInitializing(false);
    }, 3500);

    if (!hasInitialized.current) {
      syncState();
      hasInitialized.current = true;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (['SIGNED_IN', 'USER_UPDATED', 'TOKEN_REFRESHED'].includes(event)) {
        syncState(session);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSubscriptionStatus(null);
        setIsInitializing(false);
      }
    });

    const handleReactivation = () => syncState();
    window.addEventListener('pageshow', handleReactivation);
    window.addEventListener('focus', handleReactivation);
    
    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
      window.removeEventListener('pageshow', handleReactivation);
      window.removeEventListener('focus', handleReactivation);
    };
  }, [syncState, isInitializing]);

  const openAuth = (mode: 'signin' | 'signup', reason?: string) => {
    setAuthMode(mode);
    setAuthReason(reason);
    setIsAuthModalOpen(true);
    trackEvent('auth_modal_opened', { mode, reason });
  };

  const handleLogout = async () => {
    trackEvent('user_logout_initiated');
    setUser(null);
    setSubscriptionStatus(null);
    const keysToRemove = Object.keys(localStorage).filter(k => k.includes('sb-') || k.includes('supabase'));
    keysToRemove.forEach(k => localStorage.removeItem(k));
    signOut().catch(() => {});
    window.location.href = '/';
  };

  const handleAction = (plan: string) => {
    trackEvent('pricing_action_clicked', { plan, billingCycle });
    if (!user) {
      openAuth('signup', `Join 1,200+ parents and get ${plan} access.`);
      return;
    }
    if (isPro) {
      handlePortal();
    } else {
      const priceId = billingCycle === 'yearly' ? 'price_yearly_id' : 'price_monthly_id';
      createCheckoutSession(priceId, user.id, billingCycle, user.email);
    }
  };

  const handlePortal = async () => {
    trackEvent('customer_portal_requested');
    setLoadingSubscription(true);
    try { await createPortalSession(user.id); } finally { setLoadingSubscription(false); }
  };

  const scrollToSection = (id: string) => {
    trackEvent('nav_scroll_triggered', { target: id });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleBilling = (cycle: 'monthly' | 'yearly') => {
    trackEvent('billing_cycle_toggled', { cycle });
    setBillingCycle(cycle);
  };

  const isPro = subscriptionStatus?.is_pro === true;
  const prices = { monthly: 5, yearly: 4 };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-indigo-900 rounded-2xl mx-auto mb-4 animate-pulse"></div>
          <div className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Initializing...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col selection:bg-indigo-100">
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialMode={authMode} reason={authReason} />

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100 safe-top">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-100 font-black text-xl">H</div>
            <div className="text-xl font-extrabold text-slate-900 tracking-tight">Homework Bridge</div>
          </div>
          
          <div className="flex items-center gap-4 md:gap-8 text-sm font-semibold">
             <button onClick={() => scrollToSection('how-it-works')} className="text-slate-500 hover:text-indigo-700 transition-colors hidden sm:block">How it works</button>
             <button onClick={() => scrollToSection('pricing')} className="text-slate-500 hover:text-indigo-700 transition-colors hidden sm:block">Pricing</button>
             
             {user ? (
               <div className="flex items-center gap-4 sm:gap-6">
                 {isPro && <div className="px-3 py-1 bg-indigo-600 text-white rounded-full text-[9px] font-black uppercase tracking-widest">Pro</div>}
                 <span className="text-slate-900 truncate max-w-[100px] sm:max-w-[200px] font-bold text-xs">{user.email}</span>
                 <button onClick={handleLogout} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-[11px] font-bold">Log Out</button>
               </div>
             ) : (
               <div className="flex items-center gap-4">
                 <button onClick={() => openAuth('signin')} className="text-slate-500 hover:text-indigo-700 text-xs font-bold uppercase tracking-wider">Login</button>
                 <button onClick={() => openAuth('signup')} className="px-6 py-2.5 bg-indigo-900 text-white rounded-full hover:bg-indigo-950 transition-all shadow-xl shadow-indigo-100 text-xs font-bold">Get Started</button>
               </div>
             )}
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative pt-44 pb-24 px-6 overflow-hidden bg-slate-50">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200 mb-8">
            <span className="flex h-2 w-2 rounded-full bg-indigo-600 animate-pulse"></span>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Helping 1,200+ parents reclaim their evenings</span>
          </div>
          <h1 className="text-5xl md:text-[5.5rem] font-black text-slate-900 leading-[0.9] mb-8 tracking-tighter">
            Explain school topics <br />
            <span className="text-indigo-600 italic">with total confidence.</span>
          </h1>
          <p className="text-slate-500 text-lg md:text-xl font-medium max-w-2xl mx-auto mb-10 leading-relaxed">
            Stop the evening homework battles. We turn <span className="text-slate-900 font-bold">"I don't get it"</span> into <span className="text-indigo-600 font-bold">"Aha!" moments</span> in 30 seconds. No more tutors. No more friction.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
            <button 
              onClick={() => { trackEvent('hero_cta_clicked'); scrollToSection('demo-tool'); }} 
              className="w-full sm:w-auto px-12 py-6 bg-indigo-600 text-white rounded-[2rem] text-xl font-extrabold hover:bg-indigo-700 transition-all hover:scale-[1.03] shadow-2xl shadow-indigo-200"
            >
              Start Free Bridge →
            </button>
            <button 
              onClick={() => { trackEvent('hero_secondary_clicked'); scrollToSection('how-it-works'); }} 
              className="w-full sm:w-auto px-12 py-6 bg-white text-slate-600 border-2 border-slate-200 rounded-[2rem] text-xl font-extrabold hover:border-indigo-400 transition-all"
            >
              See How It Works
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-10 border-t border-slate-200/60">
            <div className="flex flex-col items-center">
               <span className="text-lg font-bold text-slate-900">1,200+</span>
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Active Parents</span>
            </div>
            <div className="flex flex-col items-center">
               <span className="text-lg font-bold text-slate-900">Grades 1-8</span>
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Curriculum Aligned</span>
            </div>
            <div className="flex flex-col items-center">
               <span className="text-lg font-bold text-slate-900">Privacy First</span>
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Safe Learning</span>
            </div>
            <div className="flex flex-col items-center">
               <span className="text-lg font-bold text-slate-900">★★★★★</span>
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Parent Approved</span>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Homework has changed. We bridge the gap.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex-shrink-0 flex items-center justify-center text-xl">📉</div>
              <div>
                <h4 className="text-lg font-bold mb-2">"New Math" Anxiety</h4>
                <p className="text-slate-500 text-sm leading-relaxed">It's not that you can't do the work—it's that they teach it differently now. We translate modern curriculum logic into plain English.</p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-xl flex-shrink-0 flex items-center justify-center text-xl">⏰</div>
              <div>
                <h4 className="text-lg font-bold mb-2">The Evening Rush</h4>
                <p className="text-slate-500 text-sm leading-relaxed">You've finished work and started dinner. You don't have time to read a 40-page textbook. We give you the "cheat sheet" in seconds.</p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-indigo-900 text-white rounded-xl flex-shrink-0 flex items-center justify-center text-xl">💸</div>
              <div>
                <h4 className="text-lg font-bold mb-2">Professional Grade Help</h4>
                <p className="text-slate-500 text-sm leading-relaxed">Why pay $50/hour for tutoring? Homework Bridge gives you the same teaching clarity for the price of one coffee a month.</p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex-shrink-0 flex items-center justify-center text-xl">🤝</div>
              <div>
                <h4 className="text-lg font-bold mb-2">Bonding Over Battles</h4>
                <p className="text-slate-500 text-sm leading-relaxed">Turn "I don't know!" shouting matches into connection moments where you are the hero who actually understands the topic.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEMO SECTION */}
      <section id="how-it-works" className="py-24 bg-indigo-950 px-6">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
          <div className="lg:w-2/5 text-white">
            <span className="text-indigo-400 font-black text-xs uppercase tracking-[0.3em] mb-6 block">Product Tour</span>
            <h2 className="text-5xl font-bold mb-8 leading-[0.95] tracking-tight text-indigo-50">Snap. Script. Success.</h2>
            <p className="text-indigo-200/70 text-lg leading-relaxed mb-12">
              We don't just solve the problem. We give you the <span className="text-white font-bold">words to say</span> and the <span className="text-white font-bold">steps to follow</span> so your child actually learns.
            </p>
            <div className="space-y-6">
               <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center font-bold text-xl text-white">1</div>
                  <p className="text-indigo-100 font-medium">Scan or type the homework question.</p>
               </div>
               <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center font-bold text-xl text-white">2</div>
                  <p className="text-indigo-100 font-medium">Receive a clear "Parent Script" and steps.</p>
               </div>
               <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center font-bold text-xl text-white">3</div>
                  <p className="text-indigo-100 font-medium">Guide them to the answer with confidence.</p>
               </div>
            </div>
          </div>
          <div className="lg:w-3/5 w-full">
            <ExplanationDemo 
              onSuggestSignup={(reason) => openAuth('signup', reason)} 
              isPro={isPro}
            />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-32 bg-white px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">Pricing for peace of mind.</h2>
            <p className="text-slate-500 font-medium">Invest in your child's success and your own sanity.</p>
          </div>

          <div className="flex items-center justify-center gap-4 mb-16">
            <button onClick={() => toggleBilling('monthly')} className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${billingCycle === 'monthly' ? 'bg-indigo-900 text-white shadow-lg' : 'text-slate-500 bg-slate-100'}`}>Monthly</button>
            <button onClick={() => toggleBilling(billingCycle === 'monthly' ? 'yearly' : 'monthly')} className={`relative w-16 h-8 rounded-full transition-all duration-300 p-1 flex items-center shadow-inner ${billingCycle === 'yearly' ? 'bg-indigo-600' : 'bg-slate-200'}`}>
              <div className={`w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-0'}`}></div>
            </button>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleBilling('yearly')} className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${billingCycle === 'yearly' ? 'bg-indigo-900 text-white shadow-lg' : 'text-slate-500 bg-slate-100'}`}>Yearly</button>
              <span className="px-2 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-widest rounded-md">SAVE 20%</span>
            </div>
          </div>

          <div className="bg-slate-50 rounded-[3rem] p-10 md:p-16 border border-slate-200/50 shadow-sm overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="pb-8 text-xl font-extrabold text-slate-900">Feature Set</th>
                  <th className="pb-8 text-center px-4">
                    <div className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mb-1">Bridge Basic</div>
                    <div className="text-2xl font-black text-slate-900">$0</div>
                  </th>
                  <th className="pb-8 text-center px-4 bg-indigo-900 rounded-t-3xl">
                    <div className="text-indigo-300 font-bold text-[10px] uppercase tracking-widest mb-1">Bridge Pro</div>
                    <div className="text-2xl font-black text-white">${billingCycle === 'yearly' ? prices.yearly : prices.monthly}<span className="text-xs font-normal opacity-70">/mo</span></div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                <tr className="border-b border-slate-100 hover:bg-white transition-colors">
                  <td className="py-6 font-semibold text-slate-800">Daily Teaching Guides</td>
                  <td className="py-6 text-center">3 Per Day</td>
                  <td className="py-6 text-center bg-indigo-900/5 text-indigo-900 font-bold">Unlimited</td>
                </tr>
                <tr className="border-b border-slate-100 hover:bg-white transition-colors">
                  <td className="py-6 font-semibold text-slate-800">Advanced OCR Scanning</td>
                  <td className="py-6 text-center">Standard</td>
                  <td className="py-6 text-center bg-indigo-900/5 text-indigo-900 font-bold">Premium High-Speed</td>
                </tr>
                <tr className="border-b border-slate-100 hover:bg-white transition-colors">
                  <td className="py-6 font-semibold text-slate-800">Teacher-Pro AI Engine</td>
                  <td className="py-6 text-center text-slate-300">Basic</td>
                  <td className="py-6 text-center bg-indigo-900/5 text-indigo-900 font-bold">Active Reasoning</td>
                </tr>
                <tr>
                  <td className="py-10"></td>
                  <td className="py-10 text-center">
                    <button onClick={() => { trackEvent('pricing_free_clicked'); scrollToSection('demo-tool'); }} className="font-bold text-slate-400 hover:text-slate-900 transition-colors">Try for Free</button>
                  </td>
                  <td className="py-10 text-center bg-indigo-900 rounded-b-3xl">
                    <button 
                      onClick={() => handleAction('Pro')} 
                      className="px-8 py-3 bg-white text-indigo-900 rounded-xl font-black shadow-lg hover:scale-105 transition-all"
                    >
                      {isPro ? 'Manage Billing' : 'Upgrade to Pro'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-slate-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100">
               <div className="text-amber-400 mb-6 text-xl">★★★★★</div>
               <p className="text-slate-600 italic mb-8 leading-relaxed">"I don't feel like a failure anymore when my 4th grader asks for help with math. The scripts are exactly what I needed."</p>
               <p className="text-slate-900 font-bold text-sm">— Sarah J., Mom of 2</p>
            </div>
            <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100">
               <div className="text-amber-400 mb-6 text-xl">★★★★★</div>
               <p className="text-slate-600 italic mb-8 leading-relaxed">"Instead of arguing for an hour, we're done in 15 minutes. It's transformed our evenings completely."</p>
               <p className="text-slate-900 font-bold text-sm">— Michael T., Dad of 3</p>
            </div>
            <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100">
               <div className="text-amber-400 mb-6 text-xl">★★★★★</div>
               <p className="text-slate-600 italic mb-8 leading-relaxed">"The photo scanning is magic. It understood messy handwriting and gave me a simple way to explain logic."</p>
               <p className="text-slate-900 font-bold text-sm">— Elena R., Busy Mom</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 bg-indigo-900 text-center px-6">
        <div className="max-w-4xl mx-auto text-white">
          <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tight">Ready for a better evening?</h2>
          <p className="text-indigo-200 text-lg mb-12 max-w-xl mx-auto font-medium">Join 1,000+ parents using Homework Bridge to make school success stress-free.</p>
          <button 
            onClick={() => { trackEvent('final_cta_clicked'); openAuth('signup'); }} 
            className="px-14 py-7 bg-white text-indigo-900 rounded-[2rem] text-xl font-black shadow-2xl hover:bg-slate-50 transition-all active:scale-95"
          >
            Start Your Free Bridge Now
          </button>
        </div>
      </section>

      <footer className="py-20 bg-white px-6 border-t border-slate-100 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="w-8 h-8 bg-indigo-900 rounded-lg flex items-center justify-center text-white font-black text-lg">H</div>
            <div className="text-lg font-black text-slate-900 tracking-tight">Homework Bridge</div>
          </div>
          <div className="flex justify-center gap-10 mb-12 text-slate-400 text-sm font-bold">
            <button className="hover:text-indigo-600 transition-colors">Privacy Policy</button>
            <button className="hover:text-indigo-600 transition-colors">Terms of Service</button>
            <button className="hover:text-indigo-600 transition-colors">Support</button>
          </div>
          <div className="text-slate-300 text-[10px] font-black uppercase tracking-[0.2em]">
             &copy; {new Date().getFullYear()} Homework Bridge • For the Modern Parent
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
